import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import { Template } from "../../models/Template";
import { useState } from "react";
import JobsRepository from "../../repositories/JobsRepository";
import { useQueryClient } from "@tanstack/react-query";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import ConfirmMigrationEntitiesExportToCsvModal, {CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS} from "../common/ConfirmMigrationEntitiesExportToCsvModal";
import {cn} from "@/lib/utils";
import { drawerTableWrapperClassName } from "../common/Table/TableStyles";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Checkbox} from "@/components/ui/checkbox";
import {Trash2} from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface CreateExportTemplatesToCsvTableProps {
  organizationId: string;
  newTemplatesForExport: Template[];
  setNewTemplatesForExport: React.Dispatch<React.SetStateAction<Template[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CreateExportTemplatesToCsvTable: React.FC<CreateExportTemplatesToCsvTableProps> = ({
  organizationId,
  newTemplatesForExport,
  setNewTemplatesForExport,
  userRole,
  isAssignedUser
}) => {
  const requiresReason = useRequiresReason();
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [exportFilesData, setExportFilesData] = useState<boolean>(false);
  const [exportReason, setExportReason] = useState<string | undefined>("");
  const allRows = [...newTemplatesForExport];
  const newIds = new Set(newTemplatesForExport.map((c) => c.id));
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  return (
    <div className="flex flex-col gap-4 pt-4 max-h-[40%] min-h-[40%]">
      <div className="flex flex-col gap-2">
        {requiresReason ? (
          <ActionConfirmationModal actionConfig={ACTION_CONFIGS.EXPORT_TEMPLATES_LIBRARY} onConfirm={(reason) => { setConfirmDeletionModalOpen(true); setExportReason(reason); }}>
            <Button disabled={!(newTemplatesForExport.length > 0)}>Export</Button>
          </ActionConfirmationModal>
        ) : (
          <Button disabled={!(newTemplatesForExport.length > 0)} onClick={() => setConfirmDeletionModalOpen(true)}>Export</Button>
        )}
        <div className="flex items-center justify-between">
          <Label className="text-sm">Export files data</Label>
          <Checkbox checked={exportFilesData} onCheckedChange={(checked) => setExportFilesData(checked as boolean)} />
        </div>
      </div>
      <div className={cn(drawerTableWrapperClassName)}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[5%] min-w-[48px] text-center" />
              <TableHead className="w-[40%]">Template Number</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {allRows.map((exportTemplateJob) => {
              return (
                <TableRow key={exportTemplateJob.id} className={newIds.has(exportTemplateJob.id) ? "bg-primary/10" : ""}>
                  <TableCell className="w-[120px] text-center" />
                  <TableCell><span className="text-sm">{exportTemplateJob.templateType}</span></TableCell>
                  <TableCell className="flex items-center justify-end px-1.5 py-3">
                    <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Cancel"
                      onClick={() => { setNewTemplatesForExport((prevRows) => prevRows.filter((entity) => entity.id !== exportTemplateJob.id)); }}>
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesExportToCsvModal open={confirmDeletionModalOpen} entityKeys={newTemplatesForExport.map((c) => c.templateType)} exportFilesData={exportFilesData} config={CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS.TEMPLATES_LIBRARIES} onClose={() => { setConfirmDeletionModalOpen(false); }}
          onConfirm={async () => {
            await jobsRepository.startCsvGenerationFromTemplates(organizationId, newTemplatesForExport.map((p) => ({id: p.id, templateType: p.templateType,})), exportFilesData, exportReason);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_TEMPLATES, organizationId] });
            setNewTemplatesForExport([]);
            setExportReason(undefined);
            setConfirmDeletionModalOpen(false);
            toast.success("Export started");
          }}
        />
      ) : (<></>)}
    </div>
  );
};

export default CreateExportTemplatesToCsvTable;
